import { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';

type Severity = 'success' | 'error' | 'info' | 'warning';

interface AlertOptions {
  persistent?: boolean;
  durationMs?: number;
}

export interface AlertItem {
  id: number;
  message: string;
  severity: Severity;
  persistent: boolean;
  durationMs: number;
}

interface AlertContextValue {
  alerts: AlertItem[];
  showAlert: (message: string, severity: Severity, opts?: AlertOptions) => void;
  dismissAlert: (id: number) => void;
  clearAll: () => void;
}

const AlertContext = createContext<AlertContextValue | null>(null);

const DEFAULT_DURATION_MS = 4000;
const ERROR_DURATION_MS = 8000;

let alertId = 0;

export function AlertProvider({ children }: { children: ReactNode }) {
  const [alerts, setAlerts] = useState<AlertItem[]>([]);

  const showAlert = useCallback(
    (message: string, severity: Severity, opts?: AlertOptions) => {
      alertId += 1;
      const persistent = opts?.persistent ?? false;
      const defaultDuration =
        severity === 'error' || severity === 'warning' ? ERROR_DURATION_MS : DEFAULT_DURATION_MS;
      const durationMs = opts?.durationMs ?? defaultDuration;
      setAlerts((prev) => [...prev, { id: alertId, message, severity, persistent, durationMs }]);
    },
    [],
  );

  const dismissAlert = useCallback((id: number) => {
    setAlerts((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const clearAll = useCallback(() => setAlerts([]), []);

  return (
    <AlertContext.Provider value={{ alerts, showAlert, dismissAlert, clearAll }}>
      {children}
    </AlertContext.Provider>
  );
}

export function useAlert(): AlertContextValue {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useAlert must be used within AlertProvider');
  return ctx;
}
